import { css } from '@emotion/react';
import { i18next, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { RadioCardField, Text } from '@frontend/design-system';
import type { EntityListItem } from '../../../../types';
import { GetRuleExceptionFormFieldProps, RuleExceptionFormTarget } from '../hooks/use-create-edit-rule-exception-form';
import { ExceptionResourcesForm } from './exception-resources-form';

const targetOptions: {
  id: RuleExceptionFormTarget;
  name: string;
  description: string;
}[] = [
  {
    id: 'ALL_RESOURCES',
    name: i18next.t('Apply to All', { ns: 'schedule-settings' }),
    description: i18next.t('All resources at this location', { ns: 'schedule-settings' }),
  },
  {
    id: 'SPECIFIC_RESOURCES',
    name: i18next.t('Apply to Chosen', { ns: 'schedule-settings' }),
    description: i18next.t('Specific resources at this location', { ns: 'schedule-settings' }),
  },
  {
    id: 'RESOURCE_COMBINATION',
    name: i18next.t('Apply to Combination', { ns: 'schedule-settings' }),
    description: i18next.t('Combination of resources at this location', { ns: 'schedule-settings' }),
  },
];

type Props = {
  providerList: EntityListItem[];
  operatoryList: EntityListItem[];
  serviceList: EntityListItem[];
  getFieldProps: GetRuleExceptionFormFieldProps;
  handleTargetChange: (newTarget: RuleExceptionFormTarget) => void;
};

export const ExceptionTargetForm = ({
  providerList,
  operatoryList,
  serviceList,
  getFieldProps,
  handleTargetChange,
}: Props) => {
  const { t } = useTranslation('schedule-settings');
  const targetFieldProps = getFieldProps('target');
  const showResourcesForm = ['SPECIFIC_RESOURCES', 'RESOURCE_COMBINATION'].includes(targetFieldProps.value);

  return (
    <>
      <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(2) }}>
        <Text weight='bold'>{t('Apply Exception To')}</Text>
      </div>
      <RadioCardField
        {...targetFieldProps}
        onChange={(e) => {
          targetFieldProps.onChange?.(e);
          const customValue = e && typeof e === 'object' && 'value' in e ? e.value : undefined;
          const targetValue =
            e && typeof e === 'object' && 'target' in e
              ? ((e.target as HTMLInputElement | HTMLButtonElement)?.value as string | undefined)
              : undefined;
          const nextTarget = customValue || targetValue;

          if (nextTarget) {
            handleTargetChange(nextTarget as RuleExceptionFormTarget);
          }
        }}
        css={radioCardFieldStyles}
      >
        {targetOptions.map((option) => (
          <RadioCardField.Option
            key={option.id}
            value={option.id}
            title={option.name}
            description={option.description}
          />
        ))}
      </RadioCardField>
      {showResourcesForm && (
        <ExceptionResourcesForm
          getFieldProps={getFieldProps}
          providerList={providerList}
          operatoryList={operatoryList}
          serviceList={serviceList}
        />
      )}
    </>
  );
};

const radioCardFieldStyles = css({
  '> div': {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: theme.spacing(2),
  },
});
